import { useRef, useState } from 'react';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../../tailwindComponents/Button';
import { Modal } from '../../../tailwindComponents/Modal';
import { useAuthHttp } from '../../../hooks';
import { INVITE_TEAM_MEMBER_URL } from '../../../constants';
import { UserRoleService } from '../../../services';
import { useAuth } from '../../../context';

export default function InviteTeamMemberDialog({
  isOpen,
  handleClose,
}: {
  isOpen: boolean;
  handleClose: () => void;
}) {
  const authHttp = useAuthHttp();
  const emailRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInviteSuccessful, setIsInviteSuccessful] = useState(false);
  const [isEmailError, setIsEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const { user } = useAuth();
  const { isWarehouseManager } = UserRoleService();

  const handleSendInvite = async () => {
    if (emailRef.current?.value) {
      setIsEmailError(false);
      setEmailErrorMessage('');
      setIsLoading(true);
      try {
        const { status } = await authHttp.post(INVITE_TEAM_MEMBER_URL, {
          email: emailRef.current?.value,
          role: isWarehouseManager(user)
            ? 'WarehouseManager'
            : 'WarehouseClient',
        });

        if (status === 200) setIsInviteSuccessful(true);
      } catch (error) {
        setIsEmailError(true);
        setEmailErrorMessage('Must be a valid email address');
        // eslint-disable-next-line no-console
        console.error('error: ', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsEmailError(true);
      setEmailErrorMessage('Email is required');
    }
  };

  const resetInviteDialog = () => {
    setIsInviteSuccessful(false);
    setIsEmailError(false);
    setEmailErrorMessage('');
    if (emailRef.current) emailRef.current.value = '';
  };

  const handleDialogClose = () => {
    handleClose();
    resetInviteDialog();
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={handleDialogClose}
      title='Invite Team Member'
      className='sm:max-w-md'
    >
      {isInviteSuccessful ? (
        <div className='text-center py-4'>
          <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4'>
            <CheckCircleIcon className='h-6 w-6 text-green-600' />
          </div>
          <p className='text-sm text-gray-600'>
            An invite has been sent to{' '}
            <span className='font-medium text-gray-900'>
              {emailRef.current?.value}
            </span>
          </p>
        </div>
      ) : (
        <div className='space-y-4'>
          <p className='text-sm text-gray-500'>
            To invite a team member, please enter their email address here. They
            will receive an email with instructions on how to join.
          </p>
          <div>
            <label
              htmlFor='email'
              className='block text-sm font-medium text-gray-900'
            >
              Email Address
            </label>
            <div className='relative mt-2'>
              <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                <EnvelopeIcon className='h-5 w-5 text-gray-400' />
              </div>
              <input
                ref={emailRef}
                type='email'
                name='email'
                id='email'
                autoFocus
                className={`block w-full rounded-lg border py-2 pl-10 pr-3 text-sm focus:outline-none transition-colors ${
                  isEmailError
                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                }`}
                placeholder='colleague@company.com'
                onFocus={() => {
                  setIsEmailError(false);
                  setEmailErrorMessage('');
                }}
              />
            </div>
            {isEmailError && emailErrorMessage && (
              <p className='mt-2 text-sm text-red-600'>{emailErrorMessage}</p>
            )}
          </div>
        </div>
      )}

      <div className='mt-6 flex justify-end gap-3'>
        <Button
          onClick={handleDialogClose}
          intent='secondary'
          size='sm'
          label={isInviteSuccessful ? 'Close' : 'Cancel'}
        />
        {isInviteSuccessful ? (
          <Button
            onClick={resetInviteDialog}
            intent='primary'
            size='sm'
            endIcon={<UserPlusIcon className='h-4 w-4' />}
            label='Invite another'
          />
        ) : (
          <Button
            onClick={handleSendInvite}
            disabled={isLoading}
            intent='primary'
            size='sm'
            endIcon={<EnvelopeIcon className='h-4 w-4' />}
            label={isLoading ? 'Sending...' : 'Send Invite'}
          />
        )}
      </div>
    </Modal>
  );
}
